import { useEffect } from 'react';

// Production domain - used for canonical URLs
const PRODUCTION_DOMAIN = 'https://graycontractingdfw.com';

/**
 * Generates proper canonical URL
 * - Uses production domain for consistent canonical URLs
 * - Normalizes path to ensure proper format
 * - Homepage returns domain without trailing slash for consistency with OG tags
 */
const getCanonicalUrl = (path) => {
  if (!path) return PRODUCTION_DOMAIN;
  
  // If full URL provided, extract path and rebuild with production domain
  if (path.startsWith('http')) {
    try {
      const url = new URL(path);
      const cleanPath = url.pathname === '/' ? '' : url.pathname;
      return `${PRODUCTION_DOMAIN}${cleanPath}`;
    } catch {
      return PRODUCTION_DOMAIN;
    }
  }
  
  // If path is root or empty, return domain without trailing slash
  if (path === '/' || path === '') {
    return PRODUCTION_DOMAIN;
  }
  
  // If path provided, ensure it starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${PRODUCTION_DOMAIN}${cleanPath}`;
};

const SEOHead = ({ title, description, keywords, canonical, robots = 'index,follow,max-image-preview:large', ogImage }) => {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && description) {
      metaDescription.setAttribute('content', description);
    }

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }

    // Update or create robots meta
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', robots);

    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && title) {
      ogTitle.setAttribute('content', title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription && description) {
      ogDescription.setAttribute('content', description);
    }

    // Update OG image if provided
    if (ogImage) {
      let ogImageMeta = document.querySelector('meta[property="og:image"]');
      if (!ogImageMeta) {
        ogImageMeta = document.createElement('meta');
        ogImageMeta.setAttribute('property', 'og:image');
        document.head.appendChild(ogImageMeta);
      }
      ogImageMeta.setAttribute('content', ogImage);
    }

    // Update OG URL to match canonical - always compute it the same way
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const computedCanonicalUrl = getCanonicalUrl(canonical || window.location.pathname);
    if (ogUrl) {
      ogUrl.setAttribute('content', computedCanonicalUrl);
    }

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle && title) {
      twitterTitle.setAttribute('content', title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription && description) {
      twitterDescription.setAttribute('content', description);
    }

    // Update canonical - CRITICAL: Must self-reference current page, not root
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = getCanonicalUrl(canonical || window.location.pathname);
    
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Cleanup: remove canonical on unmount to prevent stale values
    return () => {
      // Don't remove, just leave for next page to update
    };
  }, [title, description, keywords, canonical, robots, ogImage]);

  return null;
};

export default SEOHead;
